import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { uploadFileDocument } from '../../../api'
import {
  EMPTY_FUNCTION,
  EMPTY_FUNCTION_ASYNC,
  EMPTY_STRING,
} from '../../../constants/common'
import { useScreen } from '../../../hooks'
import { getQaSelector } from '../../../utils/common'
import Modal, { Box, Header } from '../../Modal'
import { UPLOAD_IMAGE_CROP_SHAPE } from '../UploadImage.constants'
import { useElementSize, useImageCropEngine } from '../UploadImage.hooks'
import {
  isAcceptedImageFile,
  resolveUploadImageAspect,
} from '../UploadImage.utils'

import {
  QA_NAME_UPLOAD_IMAGE,
  QA_NAME_UPLOAD_IMAGE_FILE_INPUT,
  UPLOAD_IMAGE_ACCEPT,
  UPLOAD_IMAGE_BOX_SIZE,
} from './UploadImageModal.constants'
import { useUploadImageModalSubmit } from './UploadImageModal.hooks'
import {
  isUploadImagePreviewStateError,
  isUploadImagePreviewStateLoading,
  isUploadImagePreviewStateSkeleton,
} from './UploadImageModal.utils'
import { UploadImageModalActions } from './UploadImageModalActions'
import { UploadImageModalStageArea } from './UploadImageModalStageArea'

import * as styles from './UploadImageModal.module.css'

function UploadImageModal({
  aspect,
  cropShape = UPLOAD_IMAGE_CROP_SHAPE.CIRCLE,
  documentType,
  initialFile = null,
  initialImageUrl = null,
  isResolvingImage = false,
  parent,
  // Stories/tests seam only: forces one of UPLOAD_IMAGE_PREVIEW_STATE.
  previewState = null,
  qaName = QA_NAME_UPLOAD_IMAGE,
  titleKey = 'UploadImage.AddImageTitleKey',
  withRotate = true,
  withUndo = true,
  onClose = EMPTY_FUNCTION,
  onSave = EMPTY_FUNCTION_ASYNC,
  onUpload = uploadFileDocument,
}) {
  const { t } = useTranslation()

  const { isMobileScreenSize } = useScreen()

  const fileInputRef = useRef(null)
  const [stageRef, stageSize] = useElementSize()

  const resolvedAspect = resolveUploadImageAspect({ aspect, cropShape })

  const engine = useImageCropEngine({
    aspect: resolvedAspect,
    cropShape,
    initialFile,
    initialImageUrl,
    stageHeight: stageSize.height,
    stageWidth: stageSize.width,
  })

  const { handleSubmit, isSubmitting, isUploadFailed, setUploadFailed } =
    useUploadImageModalSubmit({
      documentType,
      engine,
      parent,
      onClose,
      onSave,
      onUpload,
    })

  function handleRequestUpload() {
    fileInputRef.current?.click()
  }

  function handleChangeFile(event) {
    const [file] = event.target.files

    // Defends against a non-image picked via the OS "all files" override.
    if (isAcceptedImageFile(file)) {
      setUploadFailed(false)
      engine.handleSelectFile(file)
    }

    // Reset so picking the same file again still fires `change`.
    event.target.value = EMPTY_STRING
  }

  const isStageReady =
    engine.hasImage && Boolean(engine.crop) && Boolean(engine.layout)

  const isSkeletonState =
    isUploadImagePreviewStateSkeleton(previewState) ||
    (!previewState &&
      (isResolvingImage || Boolean(initialImageUrl)) &&
      !isStageReady &&
      !isUploadFailed &&
      !engine.isLoadError)

  const isErrorState =
    isUploadImagePreviewStateError(previewState) ||
    isUploadFailed ||
    engine.isLoadError

  const isLoadingState =
    isUploadImagePreviewStateLoading(previewState) || engine.isImageLoading

  const isBusy = isSubmitting || isLoadingState

  // Editing an existing image needs an actual change first; a freshly added
  // image is savable immediately.
  const isSaveDisabled =
    !engine.hasImage ||
    isBusy ||
    isErrorState ||
    (Boolean(initialImageUrl) && !engine.isDirty)

  const hasRotateAction = withRotate && !isErrorState
  const hasUndoAction = withUndo && engine.isDirty && !isErrorState
  // Derived from primitive booleans, not from the actions array — its onClicks
  // close over refs, which trips React Compiler's refs rule in render.
  const extraActionCount = 1 + Number(hasRotateAction) + Number(hasUndoAction)

  const isMoreCollapsed =
    isMobileScreenSize && !isSkeletonState && extraActionCount > 1

  return (
    <Modal qaName={qaName} onClose={onClose}>
      <Box
        className={styles.box}
        isFullScreen={isMobileScreenSize}
        size={isMobileScreenSize ? null : UPLOAD_IMAGE_BOX_SIZE}
      >
        <Header
          isSkeleton={isSkeletonState}
          text={t(titleKey)}
          onClose={onClose}
        />

        <UploadImageModalStageArea
          cropShape={cropShape}
          engine={engine}
          isErrorState={isErrorState}
          isLoadingState={isLoadingState}
          isMobileScreenSize={isMobileScreenSize}
          isSkeletonState={isSkeletonState}
          isSubmitting={isSubmitting}
          stageRef={stageRef}
        />

        <UploadImageModalActions
          hasImage={engine.hasImage}
          hasRotateAction={hasRotateAction}
          hasUndoAction={hasUndoAction}
          isBusy={isBusy}
          isMoreCollapsed={isMoreCollapsed}
          isSaveDisabled={isSaveDisabled}
          isSkeletonState={isSkeletonState}
          isSubmitting={isSubmitting}
          onClose={onClose}
          onReload={handleRequestUpload}
          onRotate={engine.handleRotate}
          onSubmit={handleSubmit}
          onUndo={engine.handleUndo}
        />

        <input
          {...getQaSelector({ value: QA_NAME_UPLOAD_IMAGE_FILE_INPUT })}
          accept={UPLOAD_IMAGE_ACCEPT}
          className={styles.fileInput}
          ref={fileInputRef}
          type='file'
          onChange={handleChangeFile}
        />
      </Box>
    </Modal>
  )
}

export default UploadImageModal
